import './desktop-header.less';

import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';

import classNames from 'classnames';

import Navigation from 'zoomforth_codesbane_viewer/viewer/navigation/root_container.jsx';
import InPageSearchOpener from 'zoomforth_codesbane_viewer/viewer/in_page_search_display/in_page_search_opener.jsx';
import { OpacityTransition } from 'zoomforth_component_lib/transitions.jsx';

import { VisuallyHiddenSpan } from 'zoomforth_codesbane_viewer/component_lib/visually_hidden.jsx';

import { useOnScreen } from 'zoomforth_zfutils/viewport_intersection_utils.js';
import { SocialLinks, SecondaryLogo } from '../sub_components/index.js';

import LanguageSelector from 'zoomforth_component_lib/translations_language_selector_container.jsx';


const getHeaderClass = (headerContentPosition, includeSearch) => {
  let applicableClass;

  switch (headerContentPosition) {
    case 'Left':
      applicableClass = 'navigation-links-left';
      break;
    case 'Center':
      applicableClass = 'navigation-links-center';
      break;
    case 'Right':
      applicableClass = 'navigation-links-right';
      break;
    case 'custom':
      applicableClass = includeSearch
        ? 'two-line-navigation-links-right-with-search'
        : 'two-line-navigation-links-right';
      break;
    default:
      applicableClass = '';
  }

  return applicableClass;
};

const HeaderNav = ({
  headerContentPosition,
  isInlineHeaderStyle,
  includeSearch,
}) => (
  <div
    className={classNames(
      'header-links-wrapper',
      getHeaderClass(
        !isInlineHeaderStyle && headerContentPosition === 'Right'
          ? 'custom'
          : headerContentPosition,
        includeSearch
      )
    )}
  >
    <Navigation />
  </div>
);

const FixedNav = ({
  headerContentPosition,
  searchComponent,
  includeSearch,
}) => (
  <div
    className={classNames('fixed-navigation', {
      'navigation-left-position': headerContentPosition === 'Left',
      'navigation-center-position': headerContentPosition === 'Center',
      'navigation-right-position': headerContentPosition === 'Right',
      'navigation-right-position-with-search':
        headerContentPosition === 'Right' && includeSearch,
    })}
  >
    <Navigation />
    {searchComponent && (
      <div className='search-icon-container'>{searchComponent}</div>
    )}
  </div>
);

const DesktopHeader = ({
  onHeaderLogoClick,
  pageTitle,
  shouldShowTitle,
  secondaryLogoData,
  isSecondaryLogoEnabled,
  isInlineHeaderStyle,
  headerContentPosition,
  reallyShouldShowSocialLinks,
  socialLinks,
  includeSearch,
  onInPageSearchOpenerClick,
  useFixedNavLinks,
  isFullBleed,
  isTranslationsEnabled,
  appliedLanguages,
  currentLanguageIso,
  defaultLanguageIso,
  onLanguageChange
}) => {
  // for two line display, the navigation links are never displayed in the top row
  // add-ons are flipped to the top row, when nav is dispplayed in the center
  // addons in top row when is inline or two-line with center positioning

  const displayAddOnsOnTopRow = (
    isTranslationsEnabled ||
    isInlineHeaderStyle ||
    (!isInlineHeaderStyle && headerContentPosition === 'Center')
  );

  const ref = useRef(null);
  const isVisible = useOnScreen(ref);

  return (
    <Fragment>
      <div
        ref={ref}
        className={classNames('header-wrapper-base', {
          'full-bleed': isFullBleed,
        })}
      >
        <div
          className={classNames('top-row', {
            'two-line-header': !isInlineHeaderStyle,
          })}
        >
          <div className='thumb-wrapper'>
            <a href='' className='header-thumbnail' onClick={onHeaderLogoClick}>
              <VisuallyHiddenSpan>Return home</VisuallyHiddenSpan>
            </a>
          </div>

          <p
            className={classNames('header-title-component', 'header-title')}
            title={pageTitle}
          >
            {shouldShowTitle && pageTitle}
          </p>
          {isInlineHeaderStyle && (
            <HeaderNav
              headerContentPosition={headerContentPosition}
              isInlineHeaderStyle={isInlineHeaderStyle}
            />
          )}
          <div className={'add-ons-wrapper'}>
            {displayAddOnsOnTopRow && (
              <div className={'add-ons'}>
                {
                  isTranslationsEnabled &&
                    <LanguageSelector
                      currentIso={currentLanguageIso}
                      forceRight={!reallyShouldShowSocialLinks}
                      defaultLanguageIso={defaultLanguageIso}
                      appliedLanguages={appliedLanguages}
                      onLanguageChange={onLanguageChange}
                    />
                }

                {reallyShouldShowSocialLinks && (
                  <SocialLinks pullRight={false} socialLinks={socialLinks} />
                )}
                {includeSearch && (
                  <InPageSearchOpener
                    forceRight={!reallyShouldShowSocialLinks}
                    onClick={onInPageSearchOpenerClick}
                    isEmbeded={true}
                  />
                )}
              </div>
            )}

            {!isNil(secondaryLogoData) && isSecondaryLogoEnabled && (
              <div className={'logo-right'}>
                <SecondaryLogo secondaryLogoData={secondaryLogoData} />
              </div>
            )}
          </div>
        </div>

        {!isInlineHeaderStyle && (
          <Fragment>
            <div className='header-separator' />

            <div className='bottom-row'>
              <HeaderNav
                headerContentPosition={headerContentPosition}
                isInlineHeaderStyle={isInlineHeaderStyle}
                includeSearch={includeSearch}
              />
              <div
                className={classNames('add-ons-wrapper', {
                  'width-override': headerContentPosition === 'Right',
                  'width-override-icon':
                    includeSearch && !reallyShouldShowSocialLinks,
                })}
              >
                {!displayAddOnsOnTopRow && (
                  <div className={'add-ons'}>
                    {reallyShouldShowSocialLinks && (
                      <SocialLinks
                        pullRight={false}
                        socialLinks={socialLinks}
                      />
                    )}
                    {includeSearch && (
                      <InPageSearchOpener
                        forceRight={!reallyShouldShowSocialLinks}
                        onClick={onInPageSearchOpenerClick}
                        isEmbeded={true}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </Fragment>
        )}
      </div>
      {useFixedNavLinks && !isVisible && (
        <OpacityTransition speed='fast' style={{ width: 'inherit' }}>
          <FixedNav
            headerContentPosition={headerContentPosition}
            searchComponent={
              includeSearch && (
                <InPageSearchOpener
                  forceRight={!reallyShouldShowSocialLinks}
                  onClick={onInPageSearchOpenerClick}
                  isEmbeded={true}
                />
              )
            }
            includeSearch={includeSearch}
          />
        </OpacityTransition>
      )}


    </Fragment>
  );
};

DesktopHeader.propTypes = {
  /**
   * Callback triggered when clicking on the header logo
   */
  onHeaderLogoClick: PropTypes.func,
  /**
   * The title of the current page
   */
  pageTitle: PropTypes.string.isRequired,
  /**
   * Whether the title of the page should be displayed
   */
  shouldShowTitle: PropTypes.bool.isRequired,
  /**
   */
  secondaryLogoData: PropTypes.shape({
    /**
     * Content ID
     */
    contentUuid: PropTypes.number,
    /**
     * Background size to be applied to image
     */
    backgroundSize: PropTypes.string,
    /**
     * Background position to be applied to image
     */
    backgroundPosition: PropTypes.string,
    /**
     * Object containing additional information about the image
     */
    contentItem: PropTypes.object,
    /**
     * Object containing additional information about the image. In this case
     * we are interested in the originalImage field which has the link to the image
     */
    asset: PropTypes.object,
  }),
  /**
   * Whether or not the secondary logo should be displayed
   */
  isSecondaryLogoEnabled: PropTypes.bool,
};

export default DesktopHeader;
